import { ISeriesPoint } from '../models/series-point.model';
import { TableColumn } from '../models/table-column.model';

/* -------------------------------------------------------------------------- */
/*                               ANCHOR modelos                               */
/* -------------------------------------------------------------------------- */

/** Tarjeta superior de Close The Loop: pildora de color + numero grande. */
export interface ICloseLoopKpi {
  label: string;
  value: number;
  /** Variante de <app-pill-title>. Ausente cuando el color no existe en el shared. */
  variant?: 'teal-solid' | 'purple' | 'magenta';
  /** Clase local para el unico color fuera de la paleta compartida (#05b43f). */
  pillClass?: string;
  color: string;
}

/** Fila de "Alertas pendientes por atender". */
export interface IAlertaPendiente {
  red: string;
  region: string;
  zona: string;
  sucursal: string;
  estado: string;
  recomendacion: number;
  categoria: string;
  comentario: string;
  detalle: string;
}

/** Fila de "Alertas atendidas". */
export interface IAlertaAtendida {
  region: string;
  zona: string;
  sucursal: string;
  estado: string;
  recomendacion: number;
  comentario: string;
  tipoAlerta: string;
  detalle: string;
}

/* -------------------------------------------------------------------------- */
/*                                ANCHOR KPIs                                 */
/* -------------------------------------------------------------------------- */

/** Las tres de abajo suman la de arriba; el Pulso lee de aqui sus alertas activas. */
export const CLOSE_LOOP_KPIS: Array<ICloseLoopKpi> = [
  { label: 'Recibidas',    value: 205, variant: 'teal-solid',      color: '#019c91' },
  { label: 'En proceso',   value: 3,   pillClass: 'z-pill--green', color: '#05b43f' },
  { label: 'No atendidas', value: 11,  variant: 'magenta',         color: '#ff2f92' },
  { label: 'Cerradas',     value: 191, variant: 'purple',          color: 'var(--z-purple)' },
];

/* -------------------------------------------------------------------------- */
/*                      ANCHOR historico de detractores                       */
/* -------------------------------------------------------------------------- */

/** Detractores detectados cada mes; el ultimo punto es el KPI «Recibidas». */
export const HISTORICO_DETRACTORES: Array<ISeriesPoint> = [
  { label: 'Ago-25', value: 634 },
  { label: 'Sep-25', value: 591 },
  { label: 'Oct-25', value: 668 },
  { label: 'Nov-25', value: 644 },
  { label: 'Dic-25', value: 661 },
  { label: 'Ene-26', value: 521 },
  { label: 'Feb-26', value: 546 },
  { label: 'Mar-26', value: 577 },
  { label: 'Abr-26', value: 581 },
  { label: 'May-26', value: 580 },
  { label: 'Jun-26', value: 582 },
  { label: 'Jul-26', value: 583 },
  { label: 'Ago-26', value: 205 },
];

/* -------------------------------------------------------------------------- */
/*                              ANCHOR columnas                               */
/* -------------------------------------------------------------------------- */

export const COLUMNAS_PENDIENTES: Array<TableColumn> = [
  { key: 'red',           title: 'N° RED',        headerStyle: 'teal' },
  { key: 'region',        title: 'REGIÓN',        headerStyle: 'teal' },
  { key: 'zona',          title: 'ZONA',          headerStyle: 'teal' },
  { key: 'sucursal',      title: 'SUCURSAL',      headerStyle: 'teal' },
  { key: 'estado',        title: 'ESTADO',        headerStyle: 'teal' },
  { key: 'recomendacion', title: 'RECOMENDACIÓN', headerStyle: 'teal', cellStyle: 'center' },
  { key: 'categoria',     title: 'CATEGORÍA',     headerStyle: 'teal' },
  { key: 'comentario',    title: 'COMENTARIO',    headerStyle: 'teal', width: '320px' },
  { key: 'detalle',       title: 'DETALLE',       headerStyle: 'teal', cellStyle: 'icon',
    icon: 'bi-eye', sortable: false },
];

export const COLUMNAS_ATENDIDAS: Array<TableColumn> = [
  { key: 'region',        title: 'REGIÓN',         headerStyle: 'teal' },
  { key: 'zona',          title: 'ZONA',           headerStyle: 'teal' },
  { key: 'sucursal',      title: 'SUCURSAL',       headerStyle: 'teal' },
  { key: 'estado',        title: 'ESTADO',         headerStyle: 'teal' },
  { key: 'recomendacion', title: 'RECOMENDACIÓN',  headerStyle: 'teal', cellStyle: 'center' },
  { key: 'comentario',    title: 'COMENTARIO',     headerStyle: 'teal', width: '320px' },
  { key: 'tipoAlerta',    title: 'TIPO DE ALERTA', headerStyle: 'teal' },
  { key: 'detalle',       title: 'DETALLE',        headerStyle: 'teal', cellStyle: 'icon',
    icon: 'bi-eye', sortable: false },
];

/* -------------------------------------------------------------------------- */
/*                   ANCHOR alertas pendientes por atender                    */
/* -------------------------------------------------------------------------- */

export const ALERTAS_PENDIENTES: Array<IAlertaPendiente> = [
  {
    red: '4200749',
    region: 'MARTIN MARIN',
    zona: 'SILVIA ALONSO',
    sucursal: 'SUCURSAL CHALCO',
    estado: 'ESTADO DE MÉXICO',
    recomendacion: 0,
    categoria: 'Disponibilidad de productos',
    comentario: 'El aceite de veinte litros lleva un mes agotado. Me dicen que llega el '
      + 'jueves, voy el jueves y no llegó. Ya nadie en el piso sabe darme una fecha '
      + 'real.',
    detalle: '',
  },
  {
    red: '4238427',
    region: 'ALFONSO VEGA',
    zona: 'ENRIQUE GONZALEZ',
    sucursal: 'SUCURSAL MARIANO ESCOBEDO LEON',
    estado: 'GUANAJUATO',
    recomendacion: 0,
    categoria: 'La atención del personal',
    comentario: 'El personal de mostrador me atendió de muy mala gana y tardó veinticinco '
      + 'minutos en surtir cinco productos. Nadie ofreció una disculpa por la demora.',
    detalle: '',
  },
  {
    red: '4246772',
    region: 'OTILIO MACHUCA',
    zona: 'VERONICA HERRERA',
    sucursal: 'SUCURSAL IGUALA 2',
    estado: 'GUERRERO',
    recomendacion: 2,
    categoria: 'Rapidez en el servicio',
    comentario: 'Una sola báscula funcionando en el área de granel y una fila de veinte '
      + 'personas. Tuve que irme sin comprar porque necesitaba abrir mi tienda.',
    detalle: '',
  },
  {
    red: '4306170',
    region: 'MARTIN MARIN',
    zona: 'SILVIA ALONSO',
    sucursal: 'SUCURSAL ALTA TENSION',
    estado: 'CDMX',
    recomendacion: 4,
    categoria: 'La atención del personal',
    comentario: 'Pedí ayuda para cargar dos bultos de azúcar y el empleado me dijo que él no '
      + 'hacía eso, que buscara a alguien más. Nadie más apareció y terminé '
      + 'arrastrando la mercancía hasta la banqueta.',
    detalle: '',
  },
  {
    red: '4307209',
    region: 'ATANACIO MENDOZA',
    zona: 'GUILLERMO MARTINEZ',
    sucursal: 'SUCURSAL AV. 11 SUR',
    estado: 'PUEBLA',
    recomendacion: 6,
    categoria: 'Disponibilidad de productos',
    comentario: 'El área de frutas y verduras estaba casi vacía a las once de la mañana y lo '
      + 'poco que quedaba venía golpeado. Es la tercera semana que me pasa entre '
      + 'semana.',
    detalle: '',
  },
  {
    red: '4329884',
    region: 'ALFONSO VEGA',
    zona: 'ALICIA VALENCIA',
    sucursal: 'SUCURSAL TOLUCA 1',
    estado: 'ESTADO DE MÉXICO',
    recomendacion: 4,
    categoria: 'Percepción de los precios',
    comentario: 'Las ofertas del anuncio ya no estaban vigentes pero los carteles seguían '
      + 'puestos. Cuando lo señalé me dijeron que era responsabilidad de otra área '
      + 'quitarlos.',
    detalle: '',
  },
  {
    red: '4388332',
    region: 'ALFONSO VEGA',
    zona: 'ENRIQUE GONZALEZ',
    sucursal: 'SUCURSAL PROFETA',
    estado: 'JALISCO',
    recomendacion: 5,
    categoria: 'Disponibilidad de productos',
    comentario: 'Fui por chiles secos y semillas a granel y no había existencia de casi nada. '
      + 'Me dijeron que el camión llega el viernes y el viernes seguía igual.',
    detalle: '',
  },
  {
    red: '4480950',
    region: 'MARTIN MARIN',
    zona: 'SILVIA ALONSO',
    sucursal: 'SUCURSAL LEON 2',
    estado: 'GUANAJUATO',
    recomendacion: 4,
    categoria: 'Orden y limpieza',
    comentario: 'Los baños llevan meses cerrados y el área de carga tiene basura acumulada '
      + 'junto a la mercancía. Para una tienda de este tamaño da muy mala imagen.',
    detalle: '',
  },
  {
    red: '4603525',
    region: 'ATANACIO MENDOZA',
    zona: 'ERIKA MARTINEZ',
    sucursal: 'SUCURSAL ATIZAPAN 2',
    estado: 'ESTADO DE MÉXICO',
    recomendacion: 2,
    categoria: 'La atención del personal',
    comentario: 'Fui a preguntar por una promoción del volante y me mandaron con tres '
      + 'empleados distintos. El último me dijo que esa promoción no aplicaba aquí, '
      + 'sin más explicación.',
    detalle: '',
  },
  {
    red: '4605061',
    region: 'ATANACIO MENDOZA',
    zona: 'ERIKA MARTINEZ',
    sucursal: 'SUCURSAL ARAGÓN',
    estado: 'CDMX',
    recomendacion: 2,
    categoria: 'Orden y limpieza',
    comentario: 'La cremería olía muy fuerte, había charolas con líquido derramado y el queso '
      + 'estaba fuera de refrigeración sobre una tarima de madera. No me dio nada de '
      + 'confianza.',
    detalle: '',
  },
  {
    red: '4728342',
    region: 'ALFONSO VEGA',
    zona: 'ALICIA VALENCIA',
    sucursal: 'SUCURSAL AMECAMECA',
    estado: 'ESTADO DE MÉXICO',
    recomendacion: 3,
    categoria: 'Rapidez en el servicio',
    comentario: 'Un martes a media mañana había una sola caja abierta y catorce personas '
      + 'formadas. Cuarenta minutos para pagar seis artículos, con el resto del '
      + 'personal platicando.',
    detalle: '',
  },
  {
    red: '4776844',
    region: 'ATANACIO MENDOZA',
    zona: 'GUILLERMO MARTINEZ',
    sucursal: 'SUCURSAL ALTA TENSION 3',
    estado: 'CDMX',
    recomendacion: 0,
    categoria: 'Percepción de los precios',
    comentario: 'El precio del anaquel decía ciento ochenta y en caja me cobraron doscientos '
      + 'cinco. Reclamé y la cajera me contestó que el sistema manda, que no podía '
      + 'hacer nada.',
    detalle: '',
  },
  {
    red: '4929330',
    region: 'ALFONSO VEGA',
    zona: 'ENRIQUE GONZALEZ',
    sucursal: 'SUCURSAL TULTITLAN',
    estado: 'ESTADO DE MÉXICO',
    recomendacion: 2,
    categoria: 'Rapidez en el servicio',
    comentario: 'Esperé cuarenta minutos a que abrieran el portón de carga para poder sacar '
      + 'mi pedido. El montacarguista estaba en su descanso y no había quien lo '
      + 'cubriera.',
    detalle: '',
  },
  {
    red: '5053042',
    region: 'MARTIN MARIN',
    zona: 'SILVIA ALONSO',
    sucursal: 'SUCURSAL ALTA TENSION',
    estado: 'CDMX',
    recomendacion: 6,
    categoria: 'Orden y limpieza',
    comentario: 'El refrigerador de lácteos estaba apagado y los productos a temperatura '
      + 'ambiente. Se lo dije a un empleado y me contestó que ya habían reportado la '
      + 'falla.',
    detalle: '',
  },
  {
    red: '5126356',
    region: 'LEONARDO MAXIMO',
    zona: 'ISMAEL FERMIN',
    sucursal: 'SUCURSAL AZTAHUACAN',
    estado: 'CDMX',
    recomendacion: 1,
    categoria: 'Rapidez en el servicio',
    comentario: 'El módulo de facturación me tuvo esperando cincuenta minutos y al final me '
      + 'dijeron que volviera mañana porque el sistema estaba caído. Perdí la mañana '
      + 'completa.',
    detalle: '',
  },
  {
    red: '5246121',
    region: 'ALFONSO VEGA',
    zona: 'ELEAZAR GARCIA',
    sucursal: 'SUCURSAL CAZADOR',
    estado: 'ESTADO DE MÉXICO',
    recomendacion: 4,
    categoria: 'Orden y limpieza',
    comentario: 'Los pasillos estaban bloqueados con tarimas y no se podía pasar con el '
      + 'diablito. Encima el piso estaba mojado sin señalamiento y por poco me caigo '
      + 'cargando bultos.',
    detalle: '',
  },
  {
    red: '5292306',
    region: 'OTILIO MACHUCA',
    zona: 'VERONICA HERRERA',
    sucursal: 'SUCURSAL AVENIDA CENTRAL',
    estado: 'CDMX',
    recomendacion: 1,
    categoria: 'Percepción de los precios',
    comentario: 'Subieron el frijol y el arroz de una semana a otra sin ningún aviso. Con '
      + 'esos precios ya no me sale surtirme aquí, en la central me cuesta bastante '
      + 'menos.',
    detalle: '',
  },
  {
    red: '5691827',
    region: 'OTILIO MACHUCA',
    zona: 'VERONICA HERRERA',
    sucursal: 'SUCURSAL CAZADOR NAUCALPAN',
    estado: 'ESTADO DE MÉXICO',
    recomendacion: 4,
    categoria: 'La atención del personal',
    comentario: 'Pedí una cotización de mayoreo para mi abarrote, me pidieron mi teléfono y '
      + 'nunca me llamaron. Volví a los ocho días y ni registro tenían de mi '
      + 'solicitud.',
    detalle: '',
  },
  {
    red: '5853515',
    region: 'ATANACIO MENDOZA',
    zona: 'ERIKA MARTINEZ',
    sucursal: 'SUCURSAL ALTA TENSION 2',
    estado: 'CDMX',
    recomendacion: 5,
    categoria: 'Disponibilidad de productos',
    comentario: 'Llevo cuatro visitas seguidas sin encontrar manteca en cubeta ni harina de '
      + 'veinte kilos. Surto una tortillería y me obligan a comprarle a otro '
      + 'distribuidor más caro.',
    detalle: '',
  },
  {
    red: '5880436',
    region: 'ALFONSO VEGA',
    zona: 'ELEAZAR GARCIA',
    sucursal: 'SUCURSAL ECATEPEC',
    estado: 'ESTADO DE MÉXICO',
    recomendacion: 3,
    categoria: 'Percepción de los precios',
    comentario: 'Solo me respetaron el precio de mayoreo en dos de los siete productos que '
      + 'llevaba, aunque todos pasaban del mínimo de piezas. No quisieron llamar al '
      + 'supervisor.',
    detalle: '',
  },
];

/* -------------------------------------------------------------------------- */
/*                          ANCHOR alertas atendidas                          */
/* -------------------------------------------------------------------------- */

export const ALERTAS_ATENDIDAS: Array<IAlertaAtendida> = [
  {
    region: 'ALFONSO VEGA', zona: 'ALICIA VALENCIA', sucursal: 'SUCURSAL AMECAMECA',
    estado: 'ESTADO DE MÉXICO', recomendacion: 5, tipoAlerta: 'INFORMATIVA', detalle: '',
    comentario: 'Se contactó al cliente por teléfono, se le ofreció una disculpa y el gerente '
      + 'de piso autorizó la reposición de la mercancía. Quedó conforme.',
  },
  {
    region: 'OTILIO MACHUCA', zona: 'VERONICA HERRERA', sucursal: 'SUCURSAL CAZADOR NAUCALPAN',
    estado: 'ESTADO DE MÉXICO', recomendacion: 5, tipoAlerta: 'CLIENTE QR', detalle: '',
    comentario: 'Se revisó el caso con el jefe de cremería, se recapacitó al colaborador en '
      + 'trato al cliente y se dio seguimiento con una visita del supervisor de zona.',
  },
  {
    region: 'ALFONSO VEGA', zona: 'ALICIA VALENCIA', sucursal: 'SUCURSAL TOLUCA 1',
    estado: 'ESTADO DE MÉXICO', recomendacion: 4, tipoAlerta: 'SIN CATEGORÍA', detalle: '',
    comentario: 'Se corrigió el precio en el sistema el mismo día y se hizo la devolución de '
      + 'la diferencia. El cliente confirmó que ya le respetaron el importe.',
  },
  {
    region: 'ATANACIO MENDOZA', zona: 'ERIKA MARTINEZ', sucursal: 'SUCURSAL ATIZAPAN 2',
    estado: 'ESTADO DE MÉXICO', recomendacion: 4, tipoAlerta: 'SIN COMENTARIO VÁLIDO', detalle: '',
    comentario: 'El comentario no traía texto suficiente para clasificarlo.',
  },
  {
    region: 'ALFONSO VEGA', zona: 'ELEAZAR GARCIA', sucursal: 'SUCURSAL ECATEPEC',
    estado: 'ESTADO DE MÉXICO', recomendacion: 5, tipoAlerta: '', detalle: '',
    comentario: 'Se hizo limpieza profunda del área y se reforzó la rutina de mantenimiento '
      + 'por turno. El supervisor validó el resultado en la siguiente visita.',
  },
  {
    region: 'MARTIN MARIN', zona: 'SILVIA ALONSO', sucursal: 'SUCURSAL ALTA TENSION',
    estado: 'CDMX', recomendacion: 0, tipoAlerta: 'INFORMATIVA', detalle: '',
    comentario: 'Se localizó al cliente, se le explicó la vigencia de la promoción y se le '
      + 'respetó el precio anunciado como excepción.',
  },
  {
    region: 'OTILIO MACHUCA', zona: 'VERONICA HERRERA', sucursal: 'SUCURSAL AVENIDA CENTRAL',
    estado: 'CDMX', recomendacion: 6, tipoAlerta: 'CLIENTE QR', detalle: '',
    comentario: 'Se levantó pedido extraordinario con el centro de distribución y el producto '
      + 'quedó surtido dos días después. Se avisó al cliente por WhatsApp.',
  },
  {
    region: 'MARTIN MARIN', zona: 'SILVIA ALONSO', sucursal: 'SUCURSAL LEON 2',
    estado: 'GUANAJUATO', recomendacion: 3, tipoAlerta: 'SIN CATEGORÍA', detalle: '',
    comentario: 'Se replanteó el acomodo del pasillo para dejar libre el paso de diablitos y '
      + 'se colocó señalamiento de piso mojado.',
  },
  {
    region: 'ATANACIO MENDOZA', zona: 'GUILLERMO MARTINEZ', sucursal: 'SUCURSAL ALTA TENSION 3',
    estado: 'CDMX', recomendacion: 6, tipoAlerta: 'SIN COMENTARIO VÁLIDO', detalle: '',
    comentario: 'El cliente respondió con caracteres sueltos, sin contenido interpretable.',
  },
  {
    region: 'ALFONSO VEGA', zona: 'ELEAZAR GARCIA', sucursal: 'SUCURSAL CAZADOR',
    estado: 'ESTADO DE MÉXICO', recomendacion: 2, tipoAlerta: '', detalle: '',
    comentario: 'Se llamó al cliente para tomar su cotización de mayoreo y el ejecutivo de '
      + 'cuenta le dio seguimiento directo.',
  },
  {
    region: 'ALFONSO VEGA', zona: 'ENRIQUE GONZALEZ', sucursal: 'SUCURSAL PROFETA',
    estado: 'JALISCO', recomendacion: 4, tipoAlerta: 'INFORMATIVA', detalle: '',
    comentario: 'Se dio mantenimiento correctivo al equipo de refrigeración y se retiró el '
      + 'producto que había perdido cadena de frío.',
  },
  {
    region: 'ATANACIO MENDOZA', zona: 'ERIKA MARTINEZ', sucursal: 'SUCURSAL ARAGÓN',
    estado: 'CDMX', recomendacion: 5, tipoAlerta: 'CLIENTE QR', detalle: '',
    comentario: 'Se reabrieron los sanitarios de clientes y se asignó personal de limpieza '
      + 'por turno.',
  },
  {
    region: 'MARTIN MARIN', zona: 'SILVIA ALONSO', sucursal: 'SUCURSAL CHALCO',
    estado: 'ESTADO DE MÉXICO', recomendacion: 4, tipoAlerta: 'SIN CATEGORÍA', detalle: '',
    comentario: 'Se reforzó la señalización de precios de mayoreo y se capacitó a cajas sobre '
      + 'el mínimo de piezas que aplica.',
  },
  {
    region: 'ALFONSO VEGA', zona: 'ENRIQUE GONZALEZ', sucursal: 'SUCURSAL TULTITLAN',
    estado: 'ESTADO DE MÉXICO', recomendacion: 2, tipoAlerta: 'SIN COMENTARIO VÁLIDO', detalle: '',
    comentario: 'La respuesta abierta llegó vacía; sólo se registró la calificación.',
  },
  {
    region: 'ATANACIO MENDOZA', zona: 'GUILLERMO MARTINEZ', sucursal: 'SUCURSAL AV. 11 SUR',
    estado: 'PUEBLA', recomendacion: 4, tipoAlerta: '', detalle: '',
    comentario: 'Se ajustó el horario del montacarguista para que el andén no quede sin '
      + 'cobertura durante los descansos.',
  },
  {
    region: 'OTILIO MACHUCA', zona: 'VERONICA HERRERA', sucursal: 'SUCURSAL IGUALA 2',
    estado: 'GUERRERO', recomendacion: 5, tipoAlerta: 'INFORMATIVA', detalle: '',
    comentario: 'El cliente no contestó los tres intentos de contacto; se cerró la alerta con '
      + 'la evidencia de la gestión en tienda.',
  },
  {
    region: 'ATANACIO MENDOZA', zona: 'ERIKA MARTINEZ', sucursal: 'SUCURSAL ALTA TENSION 2',
    estado: 'CDMX', recomendacion: 2, tipoAlerta: 'CLIENTE QR', detalle: '',
    comentario: 'Se retiraron los carteles de promociones vencidas de toda la tienda y se '
      + 'asignó responsable de la rotación de cenefas.',
  },
  {
    region: 'LEONARDO MAXIMO', zona: 'ISMAEL FERMIN', sucursal: 'SUCURSAL AZTAHUACAN',
    estado: 'CDMX', recomendacion: 5, tipoAlerta: 'SIN CATEGORÍA', detalle: '',
    comentario: 'Se repuso la mercancía dañada y se acordó con el cliente una revisión '
      + 'conjunta del siguiente pedido.',
  },
  {
    region: 'ALFONSO VEGA', zona: 'ENRIQUE GONZALEZ', sucursal: 'SUCURSAL MARIANO ESCOBEDO LEON',
    estado: 'GUANAJUATO', recomendacion: 4, tipoAlerta: 'SIN COMENTARIO VÁLIDO', detalle: '',
    comentario: 'El comentario no traía texto suficiente para clasificarlo.',
  },
  {
    region: 'ALFONSO VEGA', zona: 'ALICIA VALENCIA', sucursal: 'SUCURSAL AMECAMECA',
    estado: 'ESTADO DE MÉXICO', recomendacion: 5, tipoAlerta: '', detalle: '',
    comentario: 'Se escaló al área de sistemas la diferencia de precios entre anaquel y caja; '
      + 'ya quedó corregida la carga del catálogo.',
  },
  {
    region: 'OTILIO MACHUCA', zona: 'VERONICA HERRERA', sucursal: 'SUCURSAL CAZADOR NAUCALPAN',
    estado: 'ESTADO DE MÉXICO', recomendacion: 4, tipoAlerta: 'INFORMATIVA', detalle: '',
    comentario: 'Se contactó al cliente por teléfono, se le ofreció una disculpa y el gerente '
      + 'de piso autorizó la reposición de la mercancía. Quedó conforme.',
  },
  {
    region: 'ALFONSO VEGA', zona: 'ALICIA VALENCIA', sucursal: 'SUCURSAL TOLUCA 1',
    estado: 'ESTADO DE MÉXICO', recomendacion: 2, tipoAlerta: 'CLIENTE QR', detalle: '',
    comentario: 'Se revisó el caso con el jefe de cremería, se recapacitó al colaborador en '
      + 'trato al cliente y se dio seguimiento con una visita del supervisor de zona.',
  },
  {
    region: 'ATANACIO MENDOZA', zona: 'ERIKA MARTINEZ', sucursal: 'SUCURSAL ATIZAPAN 2',
    estado: 'ESTADO DE MÉXICO', recomendacion: 4, tipoAlerta: 'SIN CATEGORÍA', detalle: '',
    comentario: 'Se corrigió el precio en el sistema el mismo día y se hizo la devolución de '
      + 'la diferencia. El cliente confirmó que ya le respetaron el importe.',
  },
  {
    region: 'ALFONSO VEGA', zona: 'ELEAZAR GARCIA', sucursal: 'SUCURSAL ECATEPEC',
    estado: 'ESTADO DE MÉXICO', recomendacion: 6, tipoAlerta: 'SIN COMENTARIO VÁLIDO', detalle: '',
    comentario: 'El cliente respondió con caracteres sueltos, sin contenido interpretable.',
  },
  {
    region: 'MARTIN MARIN', zona: 'SILVIA ALONSO', sucursal: 'SUCURSAL ALTA TENSION',
    estado: 'CDMX', recomendacion: 5, tipoAlerta: '', detalle: '',
    comentario: 'Se hizo limpieza profunda del área y se reforzó la rutina de mantenimiento '
      + 'por turno. El supervisor validó el resultado en la siguiente visita.',
  },
  {
    region: 'OTILIO MACHUCA', zona: 'VERONICA HERRERA', sucursal: 'SUCURSAL AVENIDA CENTRAL',
    estado: 'CDMX', recomendacion: 3, tipoAlerta: 'INFORMATIVA', detalle: '',
    comentario: 'Se localizó al cliente, se le explicó la vigencia de la promoción y se le '
      + 'respetó el precio anunciado como excepción.',
  },
  {
    region: 'MARTIN MARIN', zona: 'SILVIA ALONSO', sucursal: 'SUCURSAL LEON 2',
    estado: 'GUANAJUATO', recomendacion: 1, tipoAlerta: 'CLIENTE QR', detalle: '',
    comentario: 'Se levantó pedido extraordinario con el centro de distribución y el producto '
      + 'quedó surtido dos días después. Se avisó al cliente por WhatsApp.',
  },
  {
    region: 'ATANACIO MENDOZA', zona: 'GUILLERMO MARTINEZ', sucursal: 'SUCURSAL ALTA TENSION 3',
    estado: 'CDMX', recomendacion: 0, tipoAlerta: 'SIN CATEGORÍA', detalle: '',
    comentario: 'Se replanteó el acomodo del pasillo para dejar libre el paso de diablitos y '
      + 'se colocó señalamiento de piso mojado.',
  },
  {
    region: 'ALFONSO VEGA', zona: 'ELEAZAR GARCIA', sucursal: 'SUCURSAL CAZADOR',
    estado: 'ESTADO DE MÉXICO', recomendacion: 2, tipoAlerta: 'SIN COMENTARIO VÁLIDO', detalle: '',
    comentario: 'La respuesta abierta llegó vacía; sólo se registró la calificación.',
  },
  {
    region: 'ALFONSO VEGA', zona: 'ENRIQUE GONZALEZ', sucursal: 'SUCURSAL PROFETA',
    estado: 'JALISCO', recomendacion: 3, tipoAlerta: '', detalle: '',
    comentario: 'Se llamó al cliente para tomar su cotización de mayoreo y el ejecutivo de '
      + 'cuenta le dio seguimiento directo.',
  },
  {
    region: 'ATANACIO MENDOZA', zona: 'ERIKA MARTINEZ', sucursal: 'SUCURSAL ARAGÓN',
    estado: 'CDMX', recomendacion: 6, tipoAlerta: 'INFORMATIVA', detalle: '',
    comentario: 'Se dio mantenimiento correctivo al equipo de refrigeración y se retiró el '
      + 'producto que había perdido cadena de frío.',
  },
  {
    region: 'MARTIN MARIN', zona: 'SILVIA ALONSO', sucursal: 'SUCURSAL CHALCO',
    estado: 'ESTADO DE MÉXICO', recomendacion: 5, tipoAlerta: 'CLIENTE QR', detalle: '',
    comentario: 'Se reabrieron los sanitarios de clientes y se asignó personal de limpieza '
      + 'por turno.',
  },
];
